
import React, { useState, useEffect } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { useAuth } from "@/context/AuthContext";
import { LocationPhoto } from "@/types/campPhoto";
import { usePhotoEditForm } from "../hooks/usePhotoEditForm";
import { PhotoEditForm } from "./photo-edit/PhotoEditForm";
import { getCampPhoto } from "@/services/campPhotoService";
import { getLocationAccessGroupById } from "@/services/locationAccessGroupService";
import { toast } from "sonner";
import { DerivativesViewer } from "./photo-edit/DerivativesViewer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";

interface PhotoEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  photo: LocationPhoto;
  photoIndex: number;
  locationId: string;
  versionDocId?: string;
  onSuccess: () => void;
}

export const PhotoEditDialog: React.FC<PhotoEditDialogProps> = ({
  isOpen,
  onClose,
  photo,
  photoIndex,
  locationId,
  versionDocId,
  onSuccess
}) => {
  const { userProfile, currentUser, refreshAuthToken } = useAuth();
  const [saving, setSaving] = useState(false);
  const [currentYOffset, setCurrentYOffset] = useState<number>(() => {
    const y = (photo as any)?.yOffset;
    return typeof y === 'number' ? y : 0;
  });
  const [error, setError] = useState<string | null>(null);
  const [permissionNote, setPermissionNote] = useState<string | null>(null);
  const [locationAccessDisplay, setLocationAccessDisplay] = useState<string | null>(null);

  const {
    form,
    handleSubmit,
    copyFromCaption
  } = usePhotoEditForm(locationId, photoIndex, photo, () => {
    setSaving(false);
    onSuccess();
    onClose();
  });

  // Update the form's yOffset value when currentYOffset changes
  React.useEffect(() => {
    form.setValue("yOffset", typeof currentYOffset === 'number' ? currentYOffset : 0);
  }, [currentYOffset, form]);

  // Fetch permission note and location access (admin display) from campPhotos when dialog opens
  useEffect(() => {
    const fetchPermissionNoteAndLocationAccess = async () => {
      if (!isOpen) return;

      const campPhotoIdVal = typeof photo.campPhotoId === "string"
        ? photo.campPhotoId
        : (photo.campPhotoId as any)?.id;

      const isPlaceholder = campPhotoIdVal === "_placeholder" || campPhotoIdVal === "campPhotos/_placeholder";
      if (isPlaceholder || !campPhotoIdVal) {
        setPermissionNote(null);
        setLocationAccessDisplay(null);
        return;
      }

      try {
        const campPhoto = await getCampPhoto(campPhotoIdVal);
        if (campPhoto?.permissionNote) {
          setPermissionNote(campPhoto.permissionNote);
        } else {
          setPermissionNote(null);
        }

        const groupId = campPhoto?.locationAccessGroupId ?? null;
        if (!groupId || typeof groupId !== "string") {
          setLocationAccessDisplay("Organization");
          return;
        }
        const group = await getLocationAccessGroupById(groupId);
        setLocationAccessDisplay(group?.name ?? groupId);
      } catch (error) {
        console.error("Error fetching permission note / location access:", error);
        setPermissionNote(null);
        setLocationAccessDisplay(null);
      }
    };

    fetchPermissionNoteAndLocationAccess();
  }, [isOpen, photo.campPhotoId]);

  const onSave = async () => {
    console.log("PhotoEditDialog: Starting save process");
    
    // Reset any previous errors
    setError(null);
    
    try {
      // Validate required fields
      const values = form.getValues();
      if (!values.photoReference) {
        toast.error("Photo credit is required");
        return;
      }
      
      // Ensure all form data has fallback values
      const processedValues = {
        photoReference: values.photoReference || photo.photoReference || "",
        caption: values.caption !== undefined ? values.caption : (photo.caption || ""),
        altText: values.altText !== undefined ? values.altText : (photo.altText || ""),
        yOffset: values.yOffset !== undefined ? values.yOffset : (currentYOffset || 0)
      };
      
      console.log("PhotoEditDialog: Form values to be submitted:", processedValues);
      
      // Ensure we have a fresh auth token before saving
      if (refreshAuthToken) {
        console.log("PhotoEditDialog: Refreshing auth token before save");
        await refreshAuthToken();
      }
      
      setSaving(true);
      await form.handleSubmit(handleSubmit)();
    } catch (error) {
      console.error("PhotoEditDialog: Error during save:", error);
      setError(error instanceof Error ? error.message : String(error));
      setSaving(false);
      toast.error("Failed to save changes");
    }
  };

  const isAdmin = userProfile?.role === "admin_user";

  const handleYOffsetChange = (value: number) => {
    console.log("PhotoEditDialog: Y-offset changed to:", value);
    setCurrentYOffset(value);
    form.setValue("yOffset", value);
  };

  // Determine placeholder vs non-placeholder
  const photoPath = (photo as any)?.path || "";
  // Heuristics:
  // - If campPhotoId is the reserved "_placeholder" doc, it's a placeholder
  // - If the download URL contains encoded folder names, detect via %2F
  const campPhotoIdVal = typeof photo.campPhotoId === 'string' 
    ? photo.campPhotoId 
    : (photo.campPhotoId as any)?.id;
  const isPlaceholderById = campPhotoIdVal === "_placeholder" || campPhotoIdVal === "campPhotos/_placeholder";
  const isPlaceholderByUrl = /\/o\/app%2F|\/app\//.test(photoPath);
  const isOrgByUrl = /\/o\/org%2F|\/org\//.test(photoPath);
  const isAppPlaceholder = isPlaceholderById || isPlaceholderByUrl;
  const isOrgPhoto = !isAppPlaceholder && (isOrgByUrl || !!campPhotoIdVal);

  return (
    <BigFormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit photo"
      saveLabel="Save changes"
      isSaving={saving}
      onSave={onSave}
    >
      <div className="space-y-6">
        {/* Admin-only debug info + asset level */}
        {isAdmin && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs font-mono text-blue-800">
              <strong>DEBUG - Photo Index {photoIndex}:</strong><br/>
              versionDocId: "{versionDocId || 'unknown'}"<br/>
              path: "{(photo as any)?.path || 'undefined'}"<br/>
              ogPath: "{(photo as any)?.ogPath || 'undefined'}"<br/>
              campPhotoId: "{typeof photo.campPhotoId === 'string' ? photo.campPhotoId : (photo as any)?.campPhotoId?.id || 'undefined'}"
            </p>
            {locationAccessDisplay !== null && (
              <>
                <div className="flex items-center gap-2 text-xs text-blue-800 mt-2">
                  <span className="font-medium">Asset level:</span>
                  <span>{locationAccessDisplay}</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex text-blue-600 hover:text-blue-900 cursor-help">
                          <HelpCircle className="h-3.5 w-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p>
                          • Organization-level assets can be used by any location but only managed by organization-level users.
                          <br/>• Group-level assets can be used only within that group&apos;s locations and managed by those group-level users or any organization-level user.
                          <br/>• This value is set when the photo is uploaded, based on the uploading user&apos;s access level.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </>
            )}
          </div>
        )}

        {/* Show positioning controls for both org photos and placeholders */}
        <PhotoEditForm
          form={form}
          isAdmin={isAdmin}
          onCopyFromCaption={copyFromCaption}
          disabled={saving}
          previewImageSrc={photo.path}
          showPositioningSection={!!photo.path}
          permissionNote={permissionNote}
        />
        
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {isAdmin && (
          <DerivativesViewer
            baseUrl={photo.path}
            widths={[120, 240, 360, 600, 1200, 1600]}
          />
        )}
      </div>
    </BigFormModal>
  );
};
